import React, { useEffect, useState, useRef } from 'react';
import { DashboardApi, DashboardConfig } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { notifyError, notifySuccess } from '../utils/statusShortcuts';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

const UserProfilePage = () => {
  const { user, accountProfile, refreshCompanies } = useAuth();
  const accountId = accountProfile?.id || user?.accountId || user?.userId || null;
  
  const [form, setForm] = useState({
    email: '',
    displayName: '',
  });
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const fileInputRef = useRef(null);
  const [originalEmail, setOriginalEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const setError = () => {};
  const [success, setSuccess] = useState(null);
  
  // Password reset state
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);

  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    taskAssignedEmail: true,
  });
  const [savingNotifications, setSavingNotifications] = useState(false);

  useEffect(() => {
    if (!accountId) {
      setLoading(false);
      setError('Kunne ikke finne kontoinformasjon.');
      notifyError('Kunne ikke finne kontoinformasjon.', { source: 'Profil' });
      return;
    }

    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const profile = await DashboardApi.getUserProfile();
        if (!isMounted) return;
        setForm({
          email: profile.email || '',
          displayName: profile.displayName || '',
        });
        setOriginalEmail(profile.email || '');
        setProfilePictureUrl(profile.profilePictureUrl || null);
      } catch (err) {
        if (!isMounted) return;
        const message = err.message || 'Kunne ikke hente brukerinformasjon.';
        setError(message);
        notifyError(message, { title: 'Kunne ikke hente profil', source: 'Profil' });
      }

      // Load notification settings separately (non-blocking)
      try {
        const notifSettings = await DashboardApi.getNotificationSettings();
        if (isMounted) {
          setNotificationSettings({
            taskAssignedEmail: notifSettings.taskAssignedEmail !== false,
          });
        }
      } catch (notifErr) {
        // Use defaults if settings can't be loaded
      }

      if (isMounted) setLoading(false);
    })();

    return () => {
      isMounted = false;
    };
  }, [accountId]);

  useEffect(() => {
    if (success) {
      notifySuccess(success, { source: 'Profil' });
      const timer = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [success]);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;

    const trimmedEmail = form.email.trim();
    const trimmedName = form.displayName.trim();

    if (!trimmedEmail) {
      setError('E-postadresse er påkrevd.');
      notifyError('E-postadresse er påkrevd.', { source: 'Profil' });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError('Ugyldig e-postadresse.');
      notifyError('Ugyldig e-postadresse.', { source: 'Profil' });
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        email: trimmedEmail,
        displayName: trimmedName || null,
      };

      const updated = await DashboardApi.updateUserProfile(payload);
      setForm({
        email: updated.email || '',
        displayName: updated.displayName || '',
      });
      setOriginalEmail(updated.email || '');
      setSuccess('Endringene ble lagret.');

      // Refresh context if email changed
      if (trimmedEmail !== originalEmail) {
        await refreshCompanies();
      }
    } catch (err) {
      const message = err.message || 'Kunne ikke lagre endringene.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke lagre profil', source: 'Profil' });
    } finally {
      setSaving(false);
    }
  };

  const emailChanged = form.email.trim() !== originalEmail;

  const handlePasswordReset = async () => {
    if (passwordResetLoading || !originalEmail) return;
    
    setPasswordResetLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: originalEmail }),
      });
      
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Kunne ikke sende tilbakestillingslenke.');
      }
      
      setPasswordResetSent(true);
      setSuccess('En lenke for å tilbakestille passordet er sendt til din e-post.');
    } catch (err) {
      setError(err.message);
      notifyError(err.message, { title: 'Kunne ikke sende lenke', source: 'Profil' });
    } finally {
      setPasswordResetLoading(false);
    }
  };

  const handlePictureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Bildet er for stort. Maks 5MB.');
      notifyError('Bildet er for stort. Maks 5MB.', { source: 'Profil' });
      return;
    }

    setUploadingPicture(true);
    setError(null);

    try {
      const result = await DashboardApi.uploadProfilePicture(file);
      // Build full URL for display
      const fullUrl = `${DashboardConfig.chatBaseUrl}${result.profilePictureUrl}`;
      setProfilePictureUrl(fullUrl);
      setSuccess('Profilbilde lastet opp.');
    } catch (err) {
      const message = err.message || 'Kunne ikke laste opp profilbilde.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke laste opp profilbilde', source: 'Profil' });
    } finally {
      setUploadingPicture(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemovePicture = async () => {
    if (!profilePictureUrl) return;
    
    setUploadingPicture(true);
    setError(null);

    try {
      await DashboardApi.removeProfilePicture();
      setProfilePictureUrl(null);
      setSuccess('Profilbilde fjernet.');
    } catch (err) {
      const message = err.message || 'Kunne ikke fjerne profilbilde.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke fjerne profilbilde', source: 'Profil' });
    } finally {
      setUploadingPicture(false);
    }
  };

  const getFullPictureUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${DashboardConfig.chatBaseUrl}${url}`;
  };

  const handleNotificationToggle = async (key) => {
    const nextValue = !notificationSettings[key];
    const optimistic = { ...notificationSettings, [key]: nextValue };
    setNotificationSettings(optimistic);

    setSavingNotifications(true);
    try {
      await DashboardApi.updateNotificationSettings(optimistic);
    } catch (err) {
      // Revert on failure
      setNotificationSettings({ ...notificationSettings });
      const message = err.message || 'Kunne ikke lagre varslingsinnstillinger.';
      notifyError(message, { title: 'Kunne ikke lagre', source: 'Profil' });
    } finally {
      setSavingNotifications(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <p className="page-eyebrow">Konto</p>
          <h1>Min profil</h1>
          <p className="page-subtitle">Administrer din personlige kontoinformasjon.</p>
        </div>
      </div>

      {/* Profile Picture Section */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2>Profilbilde</h2>
            <p className="text-muted">Bildet vises i live chat når du er agent.</p>
          </div>
        </div>
        <div className="profile-picture-section" style={{ display: 'flex', alignItems: 'center', gap: '24px', padding: '16px 0' }}>
          <div 
            className="profile-picture-preview" 
            style={{ 
              width: '96px', 
              height: '96px', 
              borderRadius: '50%', 
              backgroundColor: 'var(--color-surface, #374151)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2.5rem',
              overflow: 'hidden',
              border: '3px solid var(--color-border, #4b5563)',
            }}
          >
            {profilePictureUrl ? (
              <img 
                src={getFullPictureUrl(profilePictureUrl)} 
                alt="Profilbilde" 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <span>👤</span>
            )}
          </div>
          <div className="profile-picture-actions" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <input
              type="file"
              ref={fileInputRef}
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handlePictureUpload}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="button button-secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPicture}
              style={{ minWidth: '140px' }}
            >
              {uploadingPicture ? 'Laster opp...' : profilePictureUrl ? 'Bytt bilde' : 'Last opp bilde'}
            </button>
            {profilePictureUrl && (
              <button
                type="button"
                className="button button-danger-outline"
                onClick={handleRemovePicture}
                disabled={uploadingPicture}
                style={{ minWidth: '140px' }}
              >
                Fjern bilde
              </button>
            )}
            <p className="text-muted" style={{ fontSize: '0.75rem', margin: 0 }}>
              JPEG, PNG, GIF eller WebP. Maks 5MB.
            </p>
          </div>
        </div>
      </div>

      <form className="card" onSubmit={handleSubmit}>
        {loading ? (
          <p>Laster brukerinformasjon…</p>
        ) : (
          <>
            <div className="card-header">
              <div>
                <h2>Kontodetaljer</h2>
                <p className="text-muted">Din personlige informasjon knyttet til denne kontoen.</p>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="displayName">
                Navn
                <input
                  id="displayName"
                  className="input"
                  type="text"
                  value={form.displayName}
                  onChange={(e) => handleChange('displayName', e.target.value)}
                  placeholder="Ditt navn"
                />
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="email">
                E-postadresse
                <input
                  id="email"
                  className="input"
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="din@epost.no"
                />
              </label>
              {emailChanged && (
                <p className="text-muted" style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                  ⚠️ Du endrer e-postadressen din. Sørg for at du har tilgang til den nye adressen.
                </p>
              )}
            </div>

            <div className="form-actions" style={{ marginTop: '1.5rem' }}>
              <button type="submit" className="button button-primary" disabled={saving}>
                {saving ? 'Lagrer…' : 'Lagre endringer'}
              </button>
            </div>
          </>
        )}
      </form>

      {/* Password Reset Section */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2>Passord</h2>
            <p className="text-muted">Endre passordet ditt for å sikre kontoen.</p>
          </div>
        </div>
        <div style={{ padding: '16px 0' }}>
          {passwordResetSent ? (
            <div style={{ 
              background: 'rgba(34, 197, 94, 0.1)', 
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '12px',
              padding: '16px',
            }}>
              <p style={{ margin: 0, color: '#166534' }}>
                ✓ En e-post med tilbakestillingslenke er sendt til <strong>{originalEmail}</strong>.
                Sjekk innboksen din (og eventuelt søppelpost-mappen).
              </p>
            </div>
          ) : (
            <>
              <p className="text-muted" style={{ marginBottom: '16px' }}>
                Klikk på knappen under for å motta en e-post med lenke for å tilbakestille passordet ditt.
              </p>
              <button
                type="button"
                className="button button-secondary"
                onClick={handlePasswordReset}
                disabled={passwordResetLoading || !originalEmail}
              >
                {passwordResetLoading ? 'Sender...' : 'Tilbakestill passord'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Notification Settings */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2>Varslinger</h2>
            <p className="text-muted">Administrer hvilke e-postvarsler du ønsker å motta.</p>
          </div>
        </div>
        <div style={{ padding: '16px 0' }}>
          <label className="toggle" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              checked={notificationSettings.taskAssignedEmail}
              onChange={() => handleNotificationToggle('taskAssignedEmail')}
              disabled={savingNotifications}
            />
            <span className="toggle-track">
              <span className="toggle-thumb" />
            </span>
            <span className="toggle-label" style={{ fontSize: 'var(--text-sm, 0.875rem)' }}>
              Varsle meg på e-post når jeg blir tildelt en oppgave
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage;
